import { Box, Flex, Icon } from "@nulogy/components";
import { formatDuration, formatQuantity } from "components/helpers/format";
import NotesIndicator from "components/pages/schedule/NotesIndicator";
import Tag from "components/pages/schedule/Tag";
import eventBuilderStyles from "domain/bryntum/eventBuilders.module.scss";
import WorkBlockDomain from "domain/WorkBlock";
import WorkOrder from "domain/WorkOrder";
import React from "react";
import styled from "styled-components";
import Block from "./Block";
import BlockCellDetails from "./BlockCellDetails";
import BlockCellMenu from "./BlockCellMenu";
import BlockCellName from "./BlockCellName";
import BlockContainer from "./BlockContainer";
import CustomWorkOrderField from "./CustomWorkOrderField";
import DueDate from "./DueDate";
import FillInGapTip from "./FillInGapTip";
import formatLabor from "./formatLabor";
import Break from "./images/break.svg?react";
import LockIcon from "./LockIcon";
import WorkBlockHeader from "./WorkBlockHeader";
import WorkOrderFlag from "./WorkOrderFlag";

const QuickActionButtons = styled.div(({ hidden, theme }) => ({
  backgroundColor: theme.colors.white,
  border: `1px solid ${theme.colors.grey}`,
  borderRadius: "5px",
  bottom: 0,
  boxShadow: theme.shadows.medium,
  cursor: "pointer",
  display: hidden ? "none" : "block",
  height: "30px",
  marginBottom: theme.space.half,
  padding: "2px",
  position: "fixed",
  right: "12px",
  zIndex: 10,
}));

const TeardownTime = styled.div(({ theme }) => ({
  backgroundImage: "repeating-linear-gradient(135deg, #ddd, #ddd 10px, #eee 10px, #eee 20px)",
  color: `${theme.colors.black} !important`,
  fontWeight: theme.fontWeights.medium, // Needed to fight with Bryntum over text colors
  height: "100%",
  overflow: "hidden",
  paddingLeft: theme.space.x1,
  paddingRight: theme.space.x1,
  paddingTop: theme.space.x1,
  position: "relative",
  textOverflow: "ellipsis",
  zIndex: 2, // Keeps kabob under teardown time when very small
}));

const OverlappingPlannedBreaks = styled.span(({ theme }) => ({
  height: theme.sizes.x2,
  lineHeight: theme.sizes.x2,
}));

export default function WorkBlock({
  blockData,
  applicationContext,
  readonly,
  teardownWidth = 0,
  showOptions = true,
  highlightCustomWorkOrderFieldsIndicator = false,
}) {
  const {
    block,
    block: {
      id,
      lockedOnSchedule,
      laborOverride,
      effectiveLabor,
      jobId,
      overlappingPlannedBreaks,
      standardProductionRate,
      teardownTimeLabel,
      teardownTimeDuration,
      workOrder,
      workOrder: { code, flag, notes, unitOfMeasure },
    },
  } = blockData;
  const {
    customerSettings: { workBlockLayout },
  } = applicationContext;

  const formattedTeardownTimeDuration = formatDuration(teardownTimeDuration);
  const teardownDescription = `${teardownTimeLabel} (${formattedTeardownTimeDuration})`;
  const workOrderCardStatus = WorkOrder.cardStatus(workOrder);
  const customFieldValue = WorkOrder.getFirstCustomFieldValue(workOrder);
  const workBlockQuantity = formatQuantity(WorkBlockDomain.quantity(block));
  const jobIdMessage = `Job ${jobId}`;

  return (
    <>
      <BlockContainer
        className={`spec-work-block-${id} spec-work-block-${workOrderCardStatus}`}
        data-standard-production-rate={standardProductionRate}
        readonly={readonly}
        teardownWidth={teardownWidth}
      >
        <Block>
          {notes && <NotesIndicator />}
          <Flex flexDirection="column" height="100%" justifyContent="space-between">
            <Flex flexDirection="column" gap="2px">
              <BlockCellName highlightCustomWorkOrderFieldsIndicator={highlightCustomWorkOrderFieldsIndicator}>
                {lockedOnSchedule && (
                  <LockIcon>
                    <Icon display="block" icon="lock" size="16px" />
                  </LockIcon>
                )}
                <CustomWorkOrderField
                  description={customFieldValue?.description}
                  highlight={highlightCustomWorkOrderFieldsIndicator}
                  indicator={customFieldValue?.indicator}
                />
                <WorkBlockHeader block={block} code={code} workBlockLayout={workBlockLayout} />
              </BlockCellName>
              <BlockCellDetails>
                <DueDate workBlock={blockData.block} />
                <Box>
                  {formatLabor(laborOverride, effectiveLabor)}
                  {workBlockQuantity ? ` • ${workBlockQuantity} ${unitOfMeasure} expected` : ""}
                </Box>
              </BlockCellDetails>
            </Flex>
            <Flex alignItems="center" pt="x1">
              {jobId ? <Tag>{jobIdMessage}</Tag> : null}
              <WorkOrderFlag flag={flag} />
              <OverlappingPlannedBreaks className="overlapping-breaks">
                {overlappingPlannedBreaks?.length > 0 && (
                  <Break className={`spec-breaks-indicator ${eventBuilderStyles.breakIcon}`} size="x2" />
                )}
              </OverlappingPlannedBreaks>
            </Flex>
          </Flex>
        </Block>

        {showOptions && (
          <BlockCellMenu
            className={`b-fa b-fa-ellipsis-v blockMenu js-block-menu spec-work-block-menu pendo-work-block-menu ${eventBuilderStyles.blockMenu}`}
          />
        )}

        {teardownWidth > 0 && <TeardownTime className="teardown-time">{teardownDescription}</TeardownTime>}
      </BlockContainer>
      <Box position="absolute">
        <FillInGapTip />
        {showOptions && (
          <QuickActionButtons
            className={`quick-actions hidden spec-work-block-${id}-quick-actions`}
            id={`work-block-${id}-quick-actions`}
          >
            <Icon className="edit-notes-icon pendo-edit-notes-quick-action" color="darkBlue" icon="stickyNote" />
            <Box borderRight="1px solid grey" bottom="7px" display="inline" marginX="2px" position="relative" />
            <Icon className="fully-schedule-icon pendo-fully-schedule-quick-action" color="darkBlue" icon="fitScreen" />
          </QuickActionButtons>
        )}
      </Box>
    </>
  );
}
